import { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  Box,
  IconButton,
  Button,
  Paper,
  Fab,
  Divider,
} from "@mui/material";
import { ArrowBack, ChevronRight } from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import ProductInfoCard from "../components/ProductInfoCard";
import AccessoriesCard from "../components/AccessoriesCard";
import { useParams } from "react-router-dom";
import { useGetProductDataById, useSetActionCall } from "./services";
import ColorSelector from "../components/ColorSelector";
import EnvironmentSelector from "../components/EnvironmentSelector";
import Control from "../components/Control";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";

const theme = createTheme({
  palette: {
    primary: {
      main: "#3f51b5",
    },
    secondary: {
      main: "#f44336",
    },
  },
});

export default function Product() {
  const [rotate, setRotate] = useState(false);
  const { id } = useParams();
  const { data } = useGetProductDataById(id);
  const { mutate: sendRotateCall } = useSetActionCall();
  const { mutate: sendInteractionCall } = useSetActionCall();

  const controlData = data?.data?.showcase?.controls;
  const productData = data?.data?.showcase?.products;
  const environmentData = data?.data?.showcase?.environment;
  const mainTitle = data?.data?.showcase?.display_title;
  const modelTitle = data?.data?.showcase?.display_sub_title;
  const accessoriesData = productData?.[0]?.accessories;
  const productKey = productData?.[0]?.product_key;
  const productInfo = productData?.[0]?.additional_info;
  const actData = data?.data?.showcase?.acts;
  const interactionsData = data?.data?.showcase?.interactions;

  const controlId = data?.data?.showcase?.controls?.[0]?.control_id;

  const showcaseId = data?.data?.showcase?.showcase_id;
  const sessionId = data?.data?.sessionID;

  const [activeTab, setActiveTab] = useState(0);
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [envModalOpen, setEnvModalOpen] = useState(false);
  const [selectedAccessories, setSelectedAccessories] = useState([]);
  const [totalPrice, setTotalPrice] = useState(737000);
  const [showTerms, setShowTerms] = useState(false);
  const [selectedColor, setSelectedColor] = useState({
    name: "Solid Fire Red",
    code: "#8b0000",
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePriceClick = () => {
    setVariantModalOpen(true);
  };

  const handleAccessoryToggle = (accessory) => {
    const isAlreadySelected = selectedAccessories.some(
      (a) => a.id === accessory.id
    );

    let updated;
    if (isAlreadySelected) {
      updated = selectedAccessories.filter((a) => a.id !== accessory.id);
      setTotalPrice((prev) => prev - accessory.price);
    } else {
      updated = [...selectedAccessories, accessory];
      setTotalPrice((prev) => prev + accessory.price);
    }

    setSelectedAccessories(updated);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          height: "100vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Top Navigation */}
        <AppBar
          position="static"
          color="transparent"
          elevation={0}
          sx={{ bgcolor: "white" }}
        >
          <Toolbar>
            <IconButton edge="start" sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Box sx={{ flexGrow: 1 }}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab label="Explore" />
                <Tab label="Customise" />
              </Tabs>
            </Box>

            {/* T&C Tooltip */}
            <Box
              sx={{ position: "relative" }}
              onMouseEnter={() => setShowTerms(true)}
              onMouseLeave={() => setShowTerms(false)}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  cursor: "pointer",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                }}
              >
                T&C
              </Typography>

              {showTerms && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    mt: 1,
                    p: 2,
                    width: 300,
                    bgcolor: "white",
                    color: "text.primary",
                    boxShadow: 3,
                    borderRadius: 1,
                    zIndex: 1000,
                    fontSize: "0.75rem",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.75rem", lineHeight: 1.4 }}
                  >
                    <strong>Terms & Conditions</strong>
                    <br />
                    Accessories and features shown may not be a part of standard
                    equipment. *As certified by Test Agency Under Rule 115 (G)
                    of CMVR 1989.
                    <br />
                    <br />
                    **Car colors shown may not be available across all variants.
                    <br />
                    *Car images shown are of top variant and for illustration
                    purposes only. Accessories and features may not be part of
                    standard equipment.
                  </Typography>
                </Box>
              )}
            </Box>
          </Toolbar>
        </AppBar>

        {/* 3D Canvas and Bottom Bar */}
        <Box
          sx={{
            position: "relative",
            height: "calc(100vh - 64px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <iframe
            src={`https://canvas005.atttic.com/?showcase=${showcaseId}+&product=01+&session=${sessionId}`}
            width="100%"
            height="100%"
            frameBorder="0"
            loading="lazy"
          />

          <Fab
            size="small"
            sx={{
              position: "absolute",
              right: 20,
              bgcolor: "rgba(63, 81, 181, 0.8)",
              "&:hover": { bgcolor: "rgba(63, 81, 181, 1)" },
              zIndex: 2,
            }}
          >
            <ChevronRight />
          </Fab>

          <Paper
            elevation={3}
            sx={{
              position: "absolute",
              top: "90%",
              left: 70,
              right: 70,
              padding: "5px 15px",
              bgcolor: "white",
              zIndex: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {actData?.map((act, index) => (
                  <Box key={act.act_id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{
                          minWidth: 80,
                          textTransform: "uppercase",
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                        }}
                      >
                        {act.act_title}
                      </Button>

                      <Box sx={{ display: "flex", gap: 1 }}>
                        {act?.interactions?.map(
                          ({ interaction_id, is_visible }) => {
                            if (!is_visible) return null;

                            const interaction = interactionsData?.find(
                              (i) => i.interaction_id === interaction_id
                            );

                            if (
                              !interaction ||
                              interaction.trigger_event === "LAUNCH"
                            )
                              return null;

                            const mediumIcon =
                              interaction.interaction_icons?.find(
                                (icon) => icon.file_type === "M"
                              );
                            if (!mediumIcon) return null;

                            return (
                              <IconButton
                                key={interaction_id}
                                size="small"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  color: "text.secondary",
                                  "&:hover": { color: "primary.main" },
                                  p: 0,
                                }}
                                onClick={() => {
                                  const viewPayload = {
                                    session_id: sessionId,
                                    message: {
                                      type: "run_interaction",
                                      message: {
                                        product_key: productKey,
                                        interaction_id:
                                          interaction.interaction_id,
                                      },
                                    },
                                  };
                                  sendInteractionCall(viewPayload);
                                }}
                              >
                                <img
                                  src={mediumIcon.path}
                                  alt={interaction.display_name}
                                  title={interaction.display_name}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                  }}
                                />
                              </IconButton>
                            );
                          }
                        )}
                      </Box>
                    </Box>

                    {index < actData.length - 1 && (
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ mx: 0.3 }}
                      />
                    )}
                  </Box>
                ))}

                <Divider orientation="vertical" flexItem />
                <IconButton onClick={() => setEnvModalOpen(true)} size="small">
                  <AddPhotoAlternateIcon />
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                <ColorSelector
                  productData={productData}
                  sessionId={sessionId}
                  onChange={(color) => setSelectedColor(color)}
                />

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.25rem",
                    color: "text.primary",
                  }}
                >
                  {mainTitle}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    color: "text.secondary",
                  }}
                >
                  {modelTitle}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    "&:hover": { opacity: 0.8 },
                  }}
                  onClick={handlePriceClick}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      color: "text.primary",
                    }}
                  >
                    ₹ {totalPrice.toLocaleString("en-IN")}
                  </Typography>
                  <ChevronRight
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>

          {activeTab === 0 ? (
            <ProductInfoCard
              open={variantModalOpen}
              onClose={() => setVariantModalOpen(false)}
              productInfo={productInfo}
              mainTitle={mainTitle}
              modelTitle={modelTitle}
            />
          ) : (
            <AccessoriesCard
              open={variantModalOpen}
              onClose={() => setVariantModalOpen(false)}
              selectedAccessories={selectedAccessories}
              onToggleAccessory={handleAccessoryToggle}
              accessoriesData={accessoriesData}
            />
          )}
        </Box>

        <Control
          rotate={rotate}
          setRotate={setRotate}
          controlId={controlId}
          sessionId={sessionId}
          sendRotateCall={sendRotateCall}
          controlData={controlData}
        />

        <EnvironmentSelector
          environment={environmentData}
          open={envModalOpen}
          onClose={() => setEnvModalOpen(false)}
          sessionId={sessionId}
        />
      </Box>
    </ThemeProvider>
  );
}
